import { ELocalLawAccess } from "dto/LocalLaw/ILocalLaw";

// Model uprawnień Magistratu — metryka pracownika (Kwantum.Administration EditUserModal).
// Wartości liczbowe są kontraktem z BE (Kwantum.Enums: UserRole, SL.UserBudgetsAccessLevel,
// Cases.SpecialCaseTypeFlags, LocalLawAccess) — BE serializuje je jako liczby.

/** Kwantum.Enums.UserRole — „Uprawnienia" w metryce (flagi, w UI jedna wartość). */
export enum EUserRole {
  Normal = 0,
  Superior = 1,
  Management = 2,
  RegistryOffice = 4,
  RegistryOfficeWithPreview = 2052,
  MainAccountant = 16,
  Archivist = 32,
  Admin = 1024,
}

/** Kolejność i etykiety jak ComboItem w Magistracie (DisplayOrder). */
export const USER_ROLE_OPTIONS: { value: EUserRole; label: string; hint: string }[] =
  [
    {
      value: EUserRole.Normal,
      label: "Pracownik",
      hint: "Widzi funkcje dostępne dla wszystkich i wyłącznie własne sprawy.",
    },
    {
      value: EUserRole.Superior,
      label: "Kierownik",
      hint: "Dodatkowo „Akceptacja i Podpis” oraz zakres swojej komórki w raportach i budżecie.",
    },
    {
      value: EUserRole.Management,
      label: "Zarząd",
      hint: "Dodatkowo „Akceptacja i Podpis”, rejestry i raporty całego urzędu.",
    },
    {
      value: EUserRole.RegistryOffice,
      label: "Biuro podawcze",
      hint: "Dodatkowo „Biuro podawcze” i przesyłki wszystkich pracowników w rejestrach.",
    },
    {
      value: EUserRole.RegistryOfficeWithPreview,
      label: "Biuro podawcze wraz z podglądem",
      hint: "Jak biuro podawcze; w Magistracie dodatkowo podgląd treści dokumentów niebędących prywatnymi.",
    },
    {
      value: EUserRole.MainAccountant,
      label: "Główny księgowy",
      hint: "Dodatkowo moduł FK (księgowość).",
    },
    {
      value: EUserRole.Archivist,
      label: "Archiwista",
      hint: "Jak pracownik, a do tego zarządza wykazem akt (JRWA).",
    },
    {
      value: EUserRole.Admin,
      label: "Administrator",
      hint: "Biuro podawcze, biuro rady, pełne prawo miejscowe, FK, JRWA oraz dane całego urzędu.",
    },
  ];

export const userRoleLabel = (role: EUserRole | null | undefined) =>
  USER_ROLE_OPTIONS.find((o) => o.value === role)?.label ?? "Pracownik";

/** Kwantum.Enums.SL.UserBudgetsAccessLevel — „Widok budżetu". */
export enum EBudgetsAccessLevel {
  Default = 0,
  Department = 1,
  Office = 2,
}

export const BUDGETS_ACCESS_OPTIONS: {
  value: EBudgetsAccessLevel;
  label: string;
}[] = [
  { value: EBudgetsAccessLevel.Default, label: "Tylko własny budżet" },
  { value: EBudgetsAccessLevel.Department, label: "Budżet całego wydziału" },
  { value: EBudgetsAccessLevel.Office, label: "Budżet urzędu" },
];

/** Kwantum.Enums.Cases.SpecialCaseTypeFlags — „Programy dziedzinowe". */
export enum ESpecialCaseFlags {
  None = 0,
  Scholarship = 1,
  CouncilSession = 2,
  Tax = 4,
  Contracts = 8,
  Radix = 16,
  FK = 32,
}

/**
 * Programy dziedzinowe do nadania w metryce. Stypendia (brak modułu w Nocie) i FK (moduł
 * wynika z roli Główny księgowy / Administrator) celowo pominięte — BE też ich nie zapisuje.
 */
export const SPECIAL_CASE_OPTIONS: {
  flag: ESpecialCaseFlags;
  label: string;
  hint: string;
}[] = [
  {
    flag: ESpecialCaseFlags.Radix,
    label: "RADIX",
    hint: "Pozycja „RADIX” w menu.",
  },
  {
    flag: ESpecialCaseFlags.Contracts,
    label: "Umowy",
    hint: "Rejestr umów pokazuje umowy wszystkich pracowników, nie tylko własne.",
  },
  {
    flag: ESpecialCaseFlags.Tax,
    label: "Podatki",
    hint: "Pozycja „Podatki i opłaty” w menu.",
  },
];

/** Kwantum.Enums.LocalLawAccess — „Prawo miejscowe" (etykiety jak w EditUserModal). */
export const LOCAL_LAW_OPTIONS: { flag: ELocalLawAccess; label: string }[] = [
  { flag: ELocalLawAccess.ManageResolutions, label: "Nadzoruje uchwały" },
  { flag: ELocalLawAccess.ManageOrdinances, label: "Nadzoruje zarządzenia" },
  { flag: ELocalLawAccess.BasicManagement, label: "Nadzór ogólny" },
];

/**
 * Uprawnienia zalogowanego z /ui/privileges (pole Access) — liczone dla osoby, w której
 * imieniu pracuje (przy zastępstwie: zastępowanej).
 */
export interface IMemberAccess {
  UserRole: EUserRole;
  /** Maska UserRoleAvailability (Magistrat: User.GetRolesAvailability()). */
  RoleAvailability: number;
  LocalLawAccess: ELocalLawAccess | number;
  BudgetsAccessLevel: EBudgetsAccessLevel;
  SpecialCasePermissions: ESpecialCaseFlags | number;
  IsKSeFHandler: boolean;
  IsInAcceptationSequence: boolean;
  IsOwner: boolean;
  /** Rola Administrator albo właściciel firmy. */
  IsAdmin: boolean;
  CanEditMembers: boolean;
}
